import { useEffect, useState, useRef } from "react";
import {
  UploadSimple, FilePdf, FileImage, FileVideo, FileDoc, FileXls, File as FileIcon,
  CircleNotch, Trash, Eye, EyeSlash, DownloadSimple, X, PencilSimple, Files,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const KIND_ICON = {
  pdf:   FilePdf,
  image: FileImage,
  video: FileVideo,
  doc:   FileDoc,
  sheet: FileXls,
  text:  FileDoc,
  other: FileIcon,
};

const KIND_LABEL = {
  pdf: "PDF",
  image: "Imagem",
  video: "Vídeo",
  doc: "Documento",
  sheet: "Planilha",
  text: "Texto",
  other: "Arquivo",
};

const ACCEPT = [
  "application/pdf",
  "image/jpeg", "image/png", "image/webp", "image/gif",
  "video/mp4", "video/quicktime", "video/x-m4v", "video/webm",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  // v0.96.16 — apresentações + arquivos compactados + áudio
  "application/vnd.ms-powerpoint",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "application/zip", "application/x-zip-compressed",
  "application/x-rar-compressed", "application/vnd.rar",
  "audio/mpeg", "audio/mp4", "audio/x-m4a", "audio/wav", "audio/ogg",
  "text/plain", "text/csv",
  // Fallback pra browsers que reportam mime genérico — o backend
  // agora aceita octet-stream desde que a extensão bata.
  ".pdf,.jpg,.jpeg,.png,.webp,.gif,.mp4,.mov,.m4v,.webm,.doc,.docx",
  ".xls,.xlsx,.ppt,.pptx,.zip,.rar,.mp3,.m4a,.wav,.ogg,.txt,.csv",
].join(",");

const MAX_MB = 20;

function fmtSize(bytes) {
  if (!bytes && bytes !== 0) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
}

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("pt-BR", {
    day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

function UploadModal({ onClose, onUploaded }) {
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [status, setStatus] = useState("published");
  const [progress, setProgress] = useState(null); // 0..100 | null
  const [uploading, setUploading] = useState(false);

  const onFile = (f) => {
    if (!f) return;
    if (f.size > MAX_MB * 1024 * 1024) {
      toast.error(`Arquivo muito grande. Máx. ${MAX_MB} MB.`);
      return;
    }
    setFile(f);
    if (!title) setTitle(f.name.replace(/\.[^.]+$/, ""));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!file) {
      toast.error("Selecione um arquivo.");
      return;
    }
    if (!title.trim()) {
      toast.error("Informe um título.");
      return;
    }
    setUploading(true);
    setProgress(0);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("title", title.trim());
      if (description.trim()) fd.append("description", description.trim());
      fd.append("status", status);
      await api.post("/admin/rep-materials", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => {
          if (e.total) setProgress(Math.round((e.loaded / e.total) * 100));
        },
      });
      toast.success("Material enviado.");
      onUploaded();
    } catch (err) {
      // v0.96.16 — mensagem verbosa: mostra código HTTP + detalhe do
      // backend quando existe. Antes ficava "Falha ao enviar arquivo"
      // opaco pra qualquer erro que não fosse detail string.
      const status = err?.response?.status;
      const d = err?.response?.data?.detail;
      let msg;
      if (typeof d === "string" && d) {
        msg = status ? `${d} (HTTP ${status})` : d;
      } else if (status === 413) {
        msg = "Arquivo muito grande — limite 20 MB.";
      } else if (status === 415) {
        msg = "Tipo de arquivo não aceito.";
      } else if (status === 401 || status === 403) {
        msg = "Sessão expirada. Faça login novamente.";
      } else if (!status) {
        msg = "Conexão caiu no meio do upload. Verifique a internet e tente de novo.";
      } else {
        msg = `Falha ao enviar (HTTP ${status}).`;
      }
      toast.error(msg);
      console.error("[rep-material upload]", { status, detail: d, err });
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-[#0F0F11] border border-white/10 rounded-lg p-6 w-full max-w-lg" data-testid="rep-material-upload-modal">
        <header className="flex items-center justify-between mb-5">
          <h2 className="font-heading text-lg font-semibold tracking-tight">Novo material</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-white" data-testid="rep-material-upload-close">
            <X size={16} weight="regular" />
          </button>
        </header>

        <form onSubmit={submit} className="space-y-3">
          <label className="block cursor-pointer" htmlFor="rep-material-file">
            <span className="block text-[11px] uppercase tracking-[0.16em] text-slate-400 font-medium mb-1.5">
              Arquivo <span className="text-rose-400">*</span>
            </span>
            {/* v0.96.18 — Removido o onClick manual do <div>. Antes ele
                chamava .click() no input, MAS como o <div> já está dentro
                de um <label> associado ao mesmo input, o próprio label
                dispara o click nativamente. O onClick programático virava
                um SEGUNDO click, que em Safari/algumas versões Chrome/Edge
                cancela o primeiro → picker abre e fecha instantaneamente,
                onChange nunca dispara → modal "não faz nada". */}
            <div
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                onFile(e.dataTransfer.files?.[0]);
              }}
              className="border border-dashed border-white/20 rounded-md p-5 cursor-pointer hover:border-white/40 text-center transition-colors"
              data-testid="rep-material-dropzone"
            >
              <UploadSimple size={20} weight="regular" className="text-slate-400 mx-auto mb-2" />
              {file ? (
                <>
                  <div className="text-sm text-white truncate">{file.name}</div>
                  <div className="text-[11px] text-slate-500 mt-0.5">{fmtSize(file.size)} · {file.type || "tipo desconhecido"}</div>
                </>
              ) : (
                <>
                  <div className="text-sm text-slate-300">Clique ou arraste para enviar</div>
                  <div className="text-[11px] text-slate-500 mt-0.5">
                    PDF, imagens, vídeos, áudio, DOC/DOCX, XLS/XLSX, PPT/PPTX, ZIP · até {MAX_MB} MB
                  </div>
                </>
              )}
            </div>
            <input
              type="file"
              id="rep-material-file"
              accept={ACCEPT}
              className="hidden"
              onChange={(e) => onFile(e.target.files?.[0])}
              data-testid="rep-material-file-input"
            />
          </label>

          <label className="block">
            <span className="block text-[11px] uppercase tracking-[0.16em] text-slate-400 font-medium mb-1.5">
              Título <span className="text-rose-400">*</span>
            </span>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              data-testid="rep-material-title"
              className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-sm text-white focus:outline-none focus:border-white/30 transition-colors"
              placeholder="Ex.: Argumentos de vendas v1"
            />
          </label>

          <label className="block">
            <span className="block text-[11px] uppercase tracking-[0.16em] text-slate-400 font-medium mb-1.5">
              Descrição
            </span>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              data-testid="rep-material-description"
              className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-sm text-white focus:outline-none focus:border-white/30 resize-none transition-colors"
              placeholder="Contexto, instruções de uso, público alvo…"
            />
          </label>

          <label className="block">
            <span className="block text-[11px] uppercase tracking-[0.16em] text-slate-400 font-medium mb-1.5">
              Status inicial
            </span>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              data-testid="rep-material-status"
              className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-sm text-white focus:outline-none focus:border-white/30"
            >
              <option value="published">Publicado — visível para todos os reps</option>
              <option value="draft">Rascunho — só você vê</option>
            </select>
          </label>

          {progress !== null && (
            <div className="bg-white/5 rounded-md h-1.5 overflow-hidden" data-testid="rep-material-progress">
              <div
                className="h-full bg-emerald-400 transition-[width] duration-300 ease-out"
                style={{ width: `${progress}%` }}
              />
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="text-[13px] text-slate-400 hover:text-white px-3 py-2 transition-colors">
              Cancelar
            </button>
            <button
              type="submit"
              disabled={uploading}
              data-testid="rep-material-upload-submit"
              className="inline-flex items-center gap-2 bg-white text-slate-900 px-4 py-2 rounded-md text-sm font-semibold hover:bg-slate-200 disabled:opacity-60 transition-colors"
            >
              {uploading ? `Enviando ${progress || 0}%…` : "Enviar material"}
              {!uploading && <UploadSimple size={14} weight="regular" />}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function EditModal({ row, onClose, onSaved }) {
  const [title, setTitle] = useState(row.title || "");
  const [description, setDescription] = useState(row.description || "");
  const [status, setStatus] = useState(row.status || "published");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch(`/admin/rep-materials/${row.id}`, {
        title: title.trim(),
        description: description.trim() || null,
        status,
      });
      toast.success("Material atualizado.");
      onSaved();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Falha ao salvar.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-[#0F0F11] border border-white/10 rounded-lg p-6 w-full max-w-lg" data-testid="rep-material-edit-modal">
        <header className="flex items-center justify-between mb-5">
          <h2 className="font-heading text-lg font-semibold tracking-tight">Editar material</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-white">
            <X size={16} weight="regular" />
          </button>
        </header>
        <form onSubmit={submit} className="space-y-3">
          <label className="block">
            <span className="block text-[11px] uppercase tracking-[0.16em] text-slate-400 font-medium mb-1.5">Título</span>
            <input
              value={title} onChange={(e) => setTitle(e.target.value)} required
              className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-sm text-white focus:outline-none focus:border-white/30"
              data-testid="rep-material-edit-title"
            />
          </label>
          <label className="block">
            <span className="block text-[11px] uppercase tracking-[0.16em] text-slate-400 font-medium mb-1.5">Descrição</span>
            <textarea
              value={description} onChange={(e) => setDescription(e.target.value)} rows={3}
              className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-sm text-white focus:outline-none focus:border-white/30 resize-none"
            />
          </label>
          <label className="block">
            <span className="block text-[11px] uppercase tracking-[0.16em] text-slate-400 font-medium mb-1.5">Status</span>
            <select
              value={status} onChange={(e) => setStatus(e.target.value)}
              className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-sm text-white focus:outline-none focus:border-white/30"
              data-testid="rep-material-edit-status"
            >
              <option value="published">Publicado</option>
              <option value="draft">Rascunho</option>
            </select>
          </label>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="text-[13px] text-slate-400 hover:text-white px-3 py-2">Cancelar</button>
            <button
              type="submit" disabled={saving}
              className="inline-flex items-center gap-2 bg-white text-slate-900 px-4 py-2 rounded-md text-sm font-semibold hover:bg-slate-200 disabled:opacity-60 transition-colors"
              data-testid="rep-material-edit-submit"
            >
              {saving ? "Salvando…" : "Salvar"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

async function downloadBlob(id, filename) {
  try {
    const res = await api.get(`/admin/rep-materials/${id}/download`, {
      responseType: "blob",
    });
    const url = window.URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement("a");
    a.href = url;
    a.download = filename || "material";
    a.click();
    window.URL.revokeObjectURL(url);
  } catch {
    toast.error("Falha ao baixar.");
  }
}

export default function AdminRepMaterials() {
  const [items, setItems] = useState(null);
  const [showUpload, setShowUpload] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => {
    setItems(null);
    api.get("/admin/rep-materials").then(({ data }) => setItems(data));
  };

  useEffect(() => { load(); }, []);

  const remove = async (row) => {
    if (!window.confirm(`Remover o material "${row.title}"? Os representantes deixam de vê-lo imediatamente.`)) return;
    try {
      await api.delete(`/admin/rep-materials/${row.id}`);
      toast.success("Material removido.");
      load();
    } catch {
      toast.error("Falha ao remover.");
    }
  };

  const togglePublish = async (row) => {
    const newStatus = row.status === "published" ? "draft" : "published";
    try {
      await api.patch(`/admin/rep-materials/${row.id}`, { status: newStatus });
      toast.success(newStatus === "published" ? "Publicado." : "Movido para rascunho.");
      load();
    } catch {
      toast.error("Falha ao alterar status.");
    }
  };

  return (
    <div className="px-4 md:px-8 py-6 md:py-7 max-w-[1200px] mx-auto" data-testid="admin-rep-materials-page">
      <header className="flex items-end justify-between mb-6 flex-wrap gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-medium">
            Plataforma
          </div>
          <h1 className="font-heading text-3xl font-semibold tracking-tight text-white mt-1">
            Materiais dos Representantes
          </h1>
          <p className="text-sm text-slate-400 mt-1.5">
            Conteúdo distribuído para todos os representantes ativos —
            argumentos de vendas, PDFs do produto, vídeos institucionais,
            tabelas de preço etc.
          </p>
        </div>
        <button
          onClick={() => setShowUpload(true)}
          data-testid="admin-rep-material-new-btn"
          className="inline-flex items-center gap-2 bg-white text-slate-900 px-4 py-2 rounded-md text-sm font-semibold hover:bg-slate-200 transition-colors"
        >
          <UploadSimple size={14} weight="regular" /> Novo material
        </button>
      </header>

      {items === null ? (
        <div className="py-16 text-center">
          <CircleNotch size={20} weight="regular" className="text-slate-500 animate-spin inline-block" />
        </div>
      ) : items.length === 0 ? (
        <div className="bg-white/[0.03] border border-white/10 rounded-lg py-16 text-center text-slate-500" data-testid="admin-rep-materials-empty">
          <Files size={24} weight="regular" className="inline-block mb-3 text-slate-600" />
          <div className="text-sm">Nenhum material publicado ainda.</div>
          <div className="text-[12px] mt-1">Clique em <strong>Novo material</strong> para começar.</div>
        </div>
      ) : (
        <div className="bg-white/[0.03] border border-white/10 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-white/[0.02] text-left">
              <tr>
                <th className="px-4 py-2.5 text-[10px] uppercase tracking-[0.16em] text-slate-500 font-medium">Material</th>
                <th className="px-4 py-2.5 text-[10px] uppercase tracking-[0.16em] text-slate-500 font-medium">Tipo</th>
                <th className="px-4 py-2.5 text-[10px] uppercase tracking-[0.16em] text-slate-500 font-medium">Status</th>
                <th className="px-4 py-2.5 text-[10px] uppercase tracking-[0.16em] text-slate-500 font-medium text-right">Tamanho</th>
                <th className="px-4 py-2.5 text-[10px] uppercase tracking-[0.16em] text-slate-500 font-medium">Enviado</th>
                <th className="px-4 py-2.5 text-[10px] uppercase tracking-[0.16em] text-slate-500 font-medium text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              {items.map((m) => {
                const Icon = KIND_ICON[m.file_kind] || FileIcon;
                return (
                  <tr key={m.id} className="border-t border-white/5" data-testid={`material-row-${m.id}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-start gap-3">
                        <Icon size={20} weight="regular" className="text-slate-400 mt-0.5 shrink-0" />
                        <div className="min-w-0">
                          <div className="text-white font-medium truncate">{m.title}</div>
                          {m.description && (
                            <div className="text-[11.5px] text-slate-400 mt-0.5 line-clamp-2">{m.description}</div>
                          )}
                          <div className="text-[10px] text-slate-500 mt-1 font-mono truncate">{m.original_filename}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-[12px] text-slate-300">{KIND_LABEL[m.file_kind] || "Arquivo"}</td>
                    <td className="px-4 py-3">
                      <span className={`text-[10px] uppercase tracking-[0.14em] px-2 py-1 rounded-full ${
                        m.status === "published"
                          ? "bg-emerald-500/10 text-emerald-300"
                          : "bg-amber-500/10 text-amber-300"
                      }`}>
                        {m.status === "published" ? "publicado" : "rascunho"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right text-slate-400 tabular-nums text-[12px]">{fmtSize(m.size_bytes)}</td>
                    <td className="px-4 py-3 text-[11.5px] text-slate-400">{fmtDate(m.created_at)}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => downloadBlob(m.id, m.original_filename)}
                          title="Baixar"
                          data-testid={`material-download-${m.id}`}
                          className="p-1.5 text-slate-400 hover:text-white hover:bg-white/5 rounded-md transition-colors"
                        >
                          <DownloadSimple size={14} weight="regular" />
                        </button>
                        <button
                          onClick={() => togglePublish(m)}
                          title={m.status === "published" ? "Mover para rascunho" : "Publicar"}
                          data-testid={`material-toggle-${m.id}`}
                          className="p-1.5 text-slate-400 hover:text-white hover:bg-white/5 rounded-md transition-colors"
                        >
                          {m.status === "published" ? <EyeSlash size={14} weight="regular" /> : <Eye size={14} weight="regular" />}
                        </button>
                        <button
                          onClick={() => setEditing(m)}
                          title="Editar"
                          data-testid={`material-edit-${m.id}`}
                          className="p-1.5 text-slate-400 hover:text-white hover:bg-white/5 rounded-md transition-colors"
                        >
                          <PencilSimple size={14} weight="regular" />
                        </button>
                        <button
                          onClick={() => remove(m)}
                          title="Remover"
                          data-testid={`material-delete-${m.id}`}
                          className="p-1.5 text-slate-400 hover:text-rose-300 hover:bg-rose-500/5 rounded-md transition-colors"
                        >
                          <Trash size={14} weight="regular" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showUpload && (
        <UploadModal
          onClose={() => setShowUpload(false)}
          onUploaded={() => { setShowUpload(false); load(); }}
        />
      )}
      {editing && (
        <EditModal
          row={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}
